import { Button, Form, Input, notification, Radio, RadioChangeEvent, Select } from 'antd';
import { FormItemsWrapper, StyledForm } from './style';
import { _FORM_ITEM_LAYOUT, _TAIL_FORM_ITEM_LAYOUT } from '../../../constants';
import { useUpdateProfessionalClientContactDetails } from '../../../hooks/professionals/useClientGeneralInfo';
import { useUpdateClientContactDetails } from '../../../hooks/client/useClientGeneralInfo';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { useGetProfessionalClient } from '../../../hooks/professionals/useProfessionalClient';
import { NotificationTitle } from '../../Common/Notification/style';
import { CheckCircleOutlined } from '@ant-design/icons';
import { theme } from '../../../styles/theme';

const ContactDetailsFormInputs = () => {
  const [countries] = useSelector((state: RootState) => [state?.countries.countries]);
  return (
    <>
      <Form.Item
        name={['personalContactDetails', 'telephoneNumber']}
        label="Telephone number"
        labelAlign="left"
        rules={[{
          required: true,
          message: 'Telephone number can\'t be blank'
        }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name={['personalContactDetails', 'addressCountry']}
        label="Country"
        labelAlign="left"
        rules={[{
          required: true,
          message: 'Country can\'t be blank'
        }]}
      >
        <Select
          options={countries}
          placeholder="Please select a country"
        />
      </Form.Item>
      <Form.Item
        name={['personalContactDetails', 'addressStreet']}
        label="Street"
        labelAlign="left"
        rules={[
          {
            required: true,
            message: 'Street can\'t be blank.',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name={['personalContactDetails', 'addressNumber']}
        label="House number"
        labelAlign="left"
        rules={[{
          required: true,
          message: 'House number can\'t be blank'
        }]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name={['personalContactDetails', 'addressPlace']}
        label="Place"
        labelAlign="left"
        rules={[
          {
            required: true,
            message: 'Place can\'t be blank.',
          },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name={['personalContactDetails', 'addressPostalCode']}
        label="Postal Code"
        labelAlign="left"
        rules={[
          {
            required: true,
            message: 'Postal code can\'t be blank.',
          },
        ]}
      >
        <Input />
      </Form.Item>
    </>
  );
}

const FamilyFormInputs = () => (
  <>
    <Form.Item
      name={['familyDetailAttributes', 'maritalStatus']}
      label="Marital status"
      labelAlign="left"
      rules={[{
        required: true,
        message: 'Marital status can\'t be blank'
      }]}
    >
      <Select>
        <Select.Option value="Not married">Not married</Select.Option>
        <Select.Option value="Married">Married</Select.Option>
        <Select.Option value="Living together">Living together</Select.Option>
        <Select.Option value="Divorced">Divorced</Select.Option>
        <Select.Option value="Widow">Widow</Select.Option>
      </Select>
    </Form.Item>
  </>
);

const EducationProfessionFormInputs = () => (
  <>
    <Form.Item
      name={['careerAndEducationDetailAttributes', `profession`]}
      label="Profession"
      labelAlign="left"
      rules={[{
        required: true,
        message: 'Profession can\'t be blank'
      }]}
    >
      <Input />
    </Form.Item>
    <Form.Item
      name={['careerAndEducationDetailAttributes', 'educationLevel']}
      label="Education level"
      labelAlign="left"
      rules={[{
        required: true,
        message: 'Education level can\'t be blank'
      }]}
    >
      <Select>
        <Select.Option value="University">University</Select.Option>
        <Select.Option value="Higher vocational training">Higher vocational training</Select.Option>
        <Select.Option value="MBO / Lower vocational training">MBO / Lower vocational training</Select.Option>
        <Select.Option value="None">None</Select.Option>
      </Select>
    </Form.Item>
  </>
);

const ContactDetailsForm = () => {
  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();
  const { selectedClient } = useSelector((state: RootState) => state.professionals);
  const { user } = useSelector((state: RootState) => state.auth);
  const isClient = !!user && "professionalCode" in user;
  const { refetch: refetchClient } = useGetProfessionalClient(selectedClient?.id as string);
  const { update: updateProfessionalClientContactDetails, loading: loadingUpdateProfessionalClientContactDetails } = useUpdateProfessionalClientContactDetails();
  const { update: updateClientContactDetails, loading: loadingUpdateClientContactDetails } = useUpdateClientContactDetails();

  const withChildrenMemo = useMemo(() => {
    if (selectedClient?.familyDetail) {
      return Boolean(selectedClient?.familyDetail?.numberOfFemaleChildren > 0 || selectedClient?.familyDetail?.numberOfMaleChildren);
    }
  }, [selectedClient]);
  const [withChildren, setWithChildren] = useState<boolean>(withChildrenMemo ?? false);

  const onFinish = async (values: any) => {
    try {
      const modifiedData = {
        ...values,
        personalContactDetailAttributes: values.personalContactDetails,
        familyDetailAttributes: {
          ...values.familyDetailAttributes,
          numberOfMaleChildren: values.familyDetailAttributes?.numberOfMaleChildren ? parseInt(values.familyDetailAttributes.numberOfMaleChildren) : 0,
          numberOfFemaleChildren: values.familyDetailAttributes?.numberOfFemaleChildren ? parseInt(values.familyDetailAttributes.numberOfFemaleChildren) : 0
        }
      }
      console.log('modifiedData', modifiedData);

      const cleanData = JSON.parse(JSON.stringify(modifiedData, (key, value) => {
        if (key === '__typename') return undefined;
        return value;
      }));

      let res;
      if (isClient) {
        res = await updateClientContactDetails(cleanData);
      } else {
        res = await updateProfessionalClientContactDetails(selectedClient?.id as string, cleanData);
      }

      if (res) {
        api.info({
          message: <NotificationTitle>Client Contact details updated successfully</NotificationTitle>,
          placement: 'topRight',
          icon:
            <CheckCircleOutlined
              style={{
                color: theme.color.primary,
              }}
            />,
          style: {
            background: `${theme.bg.secondary}`,
            borderRadius: 10,
            alignItems: 'center',
            display: 'flex'
          }
        });
      }
    } catch (error: any) {
      console.error('updateContactDetails error', error);
      api.error({
        message: 'Error updating contact details',
        description: error.message,
        placement: 'topRight',
      });
    } finally {
      refetchClient();
    }
  };

  const onChangeWithChildren = ({ target: { value } }: RadioChangeEvent) => {
    setWithChildren(value);
  };

  useEffect(() => {
    if (selectedClient?.careerAndEducationDetail) {
      form.setFieldsValue({
        careerAndEducationDetailAttributes: selectedClient?.careerAndEducationDetail,
      });
    }
    if (selectedClient?.personalContactDetail) {
      form.setFieldsValue({
        personalContactDetails: selectedClient?.personalContactDetail,
      });
    }
    if (selectedClient?.familyDetail) {
      form.setFieldsValue({
        familyDetailAttributes: selectedClient?.familyDetail,
      });
    }
  }, [selectedClient, form]);

  return (
    <StyledForm
      {..._FORM_ITEM_LAYOUT}
      form={form}
      name="contact_details"
      onFinish={onFinish}
      initialValues={{}}
      style={{ marginTop: '1rem' }}
      scrollToFirstError
    >
      {contextHolder}
      <p>Contact details</p>
      <ContactDetailsFormInputs />
      <p>Education, Profession</p>
      <EducationProfessionFormInputs />
      <p>Family</p>
      <FamilyFormInputs />
      <p>Do you have children?</p>
      <Radio.Group
        options={[
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ]}
        onChange={onChangeWithChildren}
        value={withChildren}
        optionType="button"
        buttonStyle="solid"
      />
      <FormItemsWrapper hidden={!withChildren}>
        <Form.Item
          name={['familyDetailAttributes', `numberOfMaleChildren`]}
          label="How many boys?"
          labelAlign="left"
          rules={[{
            required: withChildren,
            message: 'This can\'t be blank'
          }]}
        >
          <Input type="number" />
        </Form.Item>
        <Form.Item
          name={['familyDetailAttributes', `numberOfFemaleChildren`]}
          label="How many girls?"
          labelAlign="left"
          rules={[{
            required: withChildren,
            message: 'This can\'t be blank'
          }]}
        >
          <Input type="number" />
        </Form.Item>
      </FormItemsWrapper>
      <Form.Item {..._TAIL_FORM_ITEM_LAYOUT}>
        <Button loading={isClient ? loadingUpdateClientContactDetails : loadingUpdateProfessionalClientContactDetails} type="primary" htmlType="submit">
          Save
        </Button>
      </Form.Item>
    </StyledForm>
  )
};

export default ContactDetailsForm;